import type { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import React from 'react'
import getApolloClient from 'src/apollo-client'
import {
  DIRECTORY_LOCATION,
  DIRECTORY_LOCATIONS,
  VERIFIED_NEARBY_COUNT
} from 'src/components/DirectoryFacility/directoryQuery'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import { PILLAR_PAGE_MAP } from 'src/constants/searchPages'
import Ficha from 'src/screens/Directorio/Ficha'
import {
  buildDirectoryBreadcrumbJsonLd,
  buildDirectoryLocalBusinessJsonLd
} from 'src/screens/Directorio/jsonLd'
import {
  ILocation,
  IQuery,
  IQueryDirectoryLocationsArgs,
  IQueryLocationArgs,
  IQuerySearchLocationsArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { numberWithCommas } from 'src/utils'
import {
  DIRECTORY_CATEGORY_LOCATION_TYPES,
  DIRECTORY_CATEGORY_PILLAR,
  DIRECTORY_SEO_DESCRIPTION_MAX,
  DIRECTORY_SEO_TITLE_MAX,
  DirectoryCategory,
  ISibling,
  buildDirectoryFichaHref,
  buildVerifiedNearbyHref,
  directoryCategoryForPillar,
  directoryCategoryFromLocation,
  directoryCityName,
  directoryHubCenter,
  directoryHubCityForLocation,
  fitSeoText,
  isDirectoryFichaIndexable,
  isDirectoryHubCity,
  locationIdFromDirectorySlug
} from 'src/utils/directory'
import { prettifyDirectoryText } from 'src/utils/directoryText'
import { processRequestError } from 'src/utils/errors'

const SITE = 'https://www.spotme.mx'

interface IProps {
  location: ILocation
  category: DirectoryCategory
  citySlug: string
  fichaPath: string
  siblings: ISibling[]
  hasVerifiedSupply: boolean
}

/** Never let a related-links read hold the page's own render hostage. */
const SIBLINGS_TIMEOUT_MS = 2500

/**
 * The zoom a city page searches at (`[hood]/index.tsx` → `initialFilters`).
 * The question is "does this city have ANY verified supply?", not "is there a
 * space on this block?".
 */
const VERIFIED_NEARBY_ZOOM = 11

/**
 * Does the facility's area have any verified SpotMe listing? Decides whether
 * the ficha's "Ver espacios verificados" ramps lead somewhere (2026-10-20 —
 * in a zero-supply city they landed on a page with none). An unknown answer
 * (timeout, error, no pin) returns TRUE: the ramps that predate this check
 * stay the fallback rather than vanishing on a backend blip.
 */
const fetchHasVerifiedSupply = async (
  client: ReturnType<typeof getApolloClient>,
  location: ILocation,
  category: DirectoryCategory
): Promise<boolean> => {
  const { coordinates } = location
  if (!coordinates) return true
  const pillarQuery =
    PILLAR_PAGE_MAP[DIRECTORY_CATEGORY_PILLAR[category]]?.query
  try {
    const { data } = await client.query<
      Pick<IQuery, 'searchLocations'>,
      IQuerySearchLocationsArgs
    >({
      query: VERIFIED_NEARBY_COUNT,
      variables: {
        pagination: { from: 0, pageSize: 1 },
        searchInput: {
          center: { lat: coordinates.lat, lng: coordinates.lng },
          zoom: VERIFIED_NEARBY_ZOOM,
          // The pillar page's OWN filters (size band + vehicle flag), exactly
          // as the city page sends them. `spaceTypes` alone does not narrow
          // the search: in Tijuana it matched a 3,400 m² nave and a parking
          // cajón while the mini-bodegas city page — where this ramp links —
          // shows zero (prod probe 2026-10-20).
          canStoreVehicle: pillarQuery?.canStoreVehicle,
          sizes: pillarQuery?.sizes
        }
      },
      context: {
        fetchOptions: { signal: AbortSignal.timeout(SIBLINGS_TIMEOUT_MS) }
      }
    })
    const total = data?.searchLocations?.total
    return typeof total === 'number' ? total > 0 : true
  } catch {
    return true
  }
}

/**
 * Directory ficha — `/{pillar}/{city}/directorio/{slug}`.
 *
 * Nested UNDER its pillar on purpose (Jules, 2026-08-23). Every SpotMe
 * sub-page for a topic lives under that topic's pillar — the SEO reference's
 * own examples are `/renta-mini-bodegas/uso-empresa` and
 * `/renta-mini-bodegas/spotme-vs-u-storage` — so a facility page filed under
 * `/renta-mini-bodegas/…` feeds the pillar's topical cluster instead of
 * building a separate, weaker `/directorio` silo. The category is implied by
 * the pillar, so it is no longer a path segment.
 *
 * `directorio` is a STATIC segment, which Next resolves ahead of the sibling
 * `[hood]/[configId]` listing route, so there is no ambiguity.
 *
 * Indexability (the 2026-08-25 lift, Jules) is PER ROW via
 * `isDirectoryFichaIndexable`: the three launch metros (MTY/CDMX+ZMVM/GDL —
 * the only markets where fichas share SERPs with monetized supply) stay
 * `noindex` as the phased rollout's control group, revisit ~2026-10-26 with
 * GSC evidence; and parking rows must clear a data-density gate (contact +
 * hours/price/logo) so a name-and-pin page never hits the index — the
 * thin-content drag the plan lists as risk #4. Titles/descriptions are
 * budget-fitted (`fitSeoText`) so a long facility name truncates instead of
 * the disclaimer or brand suffix falling off the SERP. Indexable fichas are
 * enumerated into `pages/sitemap/search.xml.tsx` by the SAME predicate — one
 * policy, two consumers.
 *
 * Server-rendered rather than static: directory rows arrive by import and can
 * be opted out at any time, and a removal must stop serving the page within
 * 24h (plan §3.6) — a statically generated ficha would keep serving from the
 * edge until something revalidated it.
 */
const DirectoryFichaPage: NextPage<IProps> = ({
  location,
  category,
  citySlug,
  fichaPath,
  siblings,
  hasVerifiedSupply
}) => {
  const { t } = useTranslation('directorio')

  // `getServerSideProps` below is typed `IProps | {}` and every "nothing here"
  // branch returns `props: {}` + a redirect — six of them, because a directory
  // row can vanish in six ways. Both redirect paths resolve before this renders
  // in every case I could reproduce (a hard navigation 307s at the edge; a
  // client transition consumes `__N_REDIRECT`), yet prod caught one render with
  // empty props anyway, from a crawler, on a slug whose location no longer
  // exists (Datadog issue fd492468, 2026-09-09). Unguarded, the very next line
  // dereferences `location` and takes down the whole page with a TypeError.
  // Guard rather than hunt the last edge path: the props type already says they
  // can be empty, so the component must not pretend otherwise. Mirrors the same
  // guard on the sibling `[configId]/index.tsx` and `[configId]/report.tsx`.
  if (!location) return null

  const name = prettifyDirectoryText(location.title)
  const cityName = directoryCityName(citySlug)
  const categoryShortLabel =
    category === DirectoryCategory.Storage
      ? t('CATEGORY_SHORT.bodegas')
      : t('CATEGORY_SHORT.estacionamientos')
  // Emitted even though the page is `noindex` at launch, for the same reason
  // as the LocalBusiness block: both are already correct the day that lifts.
  const localBusiness = buildDirectoryLocalBusinessJsonLd(location, fichaPath)
  // Mirrors the visible crumb: the parent is the verified supply page for this
  // metro + category (the directory has no browsable index), and the last
  // entry is the current page, as BreadcrumbList requires.
  const breadcrumbs = buildDirectoryBreadcrumbJsonLd([
    {
      name: t('FICHA.BREADCRUMB_VERIFIED', {
        category: categoryShortLabel,
        city: cityName
      }),
      path: buildVerifiedNearbyHref(category, citySlug, location.coordinates)
    },
    { name, path: fichaPath }
  ])

  // The 2026-08-25 lift: indexability is per-row policy now (held launch
  // metros + the parking data-density gate), and the title/description are
  // budget-fitted so a long facility name truncates instead of the disclaimer
  // or brand suffix falling off the SERP.
  const indexable = isDirectoryFichaIndexable({ citySlug, category, location })
  const seoCategoryLabel =
    category === DirectoryCategory.Storage
      ? t('FICHA.SEO_CATEGORY.bodegas')
      : t('FICHA.SEO_CATEGORY.estacionamientos')
  const seoTitle = fitSeoText(
    [
      (n) =>
        t('FICHA.SEO_TITLE_FULL', {
          name: n,
          category: seoCategoryLabel,
          city: cityName
        }),
      (n) => t('FICHA.SEO_TITLE_SHORT', { name: n, city: cityName })
    ],
    name,
    DIRECTORY_SEO_TITLE_MAX
  )
  const seoPrice = location.directoryMeta?.priceFromMxn
  const seoDescription = fitSeoText(
    [
      ...(seoPrice
        ? [
            (n: string) =>
              t('FICHA.SEO_DESCRIPTION_PRICE', {
                name: n,
                category: seoCategoryLabel,
                city: cityName,
                price: numberWithCommas(seoPrice)
              })
          ]
        : []),
      (n: string) =>
        t('FICHA.SEO_DESCRIPTION', {
          name: n,
          category: seoCategoryLabel,
          city: cityName
        })
    ],
    name,
    DIRECTORY_SEO_DESCRIPTION_MAX
  )

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={`${SITE}${fichaPath}`}
        noindex={!indexable}
        nofollow={!indexable}
        // `openGraph` must be present as an OBJECT or next-seo emits NO
        // `og:url` at all — its emitter is `if (config.openGraph) { if
        // (openGraph.url || canonical) … }`, so a page that passes only
        // `canonical` never reaches the inner branch. Every other page in the
        // repo passes one; this page did not, which made all 168 indexable
        // fichas "Open Graph tags incomplete" in the Ahrefs site audit
        // (2026-08-28). `site_name`, `locale`, `type` and the cover image
        // still come from `DefaultSeo` — next/head dedupes on the shared key.
        openGraph={{
          type: 'website',
          url: `${SITE}${fichaPath}`,
          title: seoTitle,
          description: seoDescription
        }}
      />
      <Head>
        {localBusiness && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusiness) }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbs) }}
        />
      </Head>
      <LandingNav />
      <Ficha
        location={location}
        category={category}
        citySlug={citySlug}
        fichaPath={fichaPath}
        siblings={siblings}
        hasVerifiedSupply={hasVerifiedSupply ?? true}
      />
      <Footer />
    </>
  )
}

/**
 * Where a ficha sends a visitor when there is nothing to show.
 *
 * Jules, 2026-08-23: a directory URL must never dead-end on a 404. Rows are
 * retired (opt-out, re-import, a claim converting one into a real listing), and
 * the person who followed that link still wants storage in that city — so hand
 * them the verified supply page instead of a wall.
 *
 * `buildVerifiedNearbyHref` already resolves to a real page in every case: the
 * pillar+city landing page, else a geo-anchored `/search`, else the bare
 * pillar. Only an unrecognised pillar has no better answer than `/search`.
 */
const directoryFallbackHref = (
  pillar: string | string[] | undefined,
  city: string | string[] | undefined,
  coordinates?: { lat: number; lng: number } | null
): string => {
  const category = directoryCategoryForPillar(pillar)
  if (!category) return RedirectUrls.Search
  return buildVerifiedNearbyHref(
    category,
    Array.isArray(city) ? city[0] : city,
    coordinates
  )
}

/**
 * Temporary (307), never permanent, for every "nothing here" redirect below.
 * A retired row can come back — re-imported, or claimed and reborn as a real
 * listing on its own URL — and browsers cache a 308 indefinitely, which would
 * make that URL un-servable long after the row returned. The canonical-path
 * redirect further down stays permanent because that one IS forever.
 */
const goneRedirect = (destination: string) => ({
  props: {},
  redirect: { destination, permanent: false }
})

/**
 * How many sibling fichas the block below links to, and how wide it looks.
 *
 * `SIBLINGS_ZOOM` 9 is a deliberate OVERSHOOT, not a "city box". A zoom-10 box
 * is +-63km east-west but only +-49km north-south, while a facility routes
 * under a metro at up to `DIRECTORY_HUB_RADIUS_KM` (80km) in ANY direction —
 * so the box that looks like it matches the routing rule actually clips the
 * north-south corners. Los Cabos and Saltillo lost rows to exactly that gap on
 * the 2026-08-28 re-crawl. Zoom 9 (+-126 / +-98km) is a strict superset; the
 * `directoryHubCityForLocation` filter below, not the geometry, is what decides
 * membership.
 *
 * `SIBLINGS_POOL` is sized to hold the whole overshoot box before filtering
 * (Toluca's zoom-9 box carries ~96 storage rows because it reaches into the
 * ZMVM). Truncation is harmless even if a denser vertical ever exceeds it: the
 * query is IDENTICAL for every ficha in a city, so all of them see the same
 * truncated pool and the ring stays symmetric.
 *
 * Only `SIBLINGS_SHOWN` of the pool ever reaches `__NEXT_DATA__`.
 */
const SIBLINGS_SHOWN = 6
const SIBLINGS_POOL = 200
const SIBLINGS_ZOOM = 9

/**
 * The other directory fichas in this city — the fix for 168 ORPHAN PAGES
 * (Ahrefs site audit, 2026-08-28).
 *
 * The 2026-08-25 noindex lift made non-launch-metro fichas indexable AND put
 * them in `sitemap/search.xml`, but left the link graph as the plan wrote it:
 * the ONLY thing that ever linked to a ficha was the tier floor inside search,
 * which is `dynamic(..., { ssr: false })` behind an IntersectionObserver. No
 * crawler scrolls, so every one of those 168 indexable, sitemapped URLs had
 * zero incoming internal links — an ERROR-severity issue on all of them, and
 * the bulk of the health-score drop.
 *
 * Sibling links close it from inside the two surfaces the plan kept. This is
 * NOT the browsable directory index that was built and deliberately removed on
 * 2026-08-23 (plan: "Do not re-add a browsable directory index without
 * re-opening the cannibalization question") — there is no new URL, no new
 * hub, and nothing here targets "{categoria} en {ciudad}", the head query that
 * made the old hubs cannibalize `/{pillar}/{city}`. It is a related-links block
 * on a page that already exists.
 *
 * Filtered through `isDirectoryFichaIndexable` — the SAME predicate the ficha's
 * own `noindex` flag and the sitemap use, so this becomes its third consumer
 * and cannot drift: a held-metro ficha (MTY/CDMX/GDL) links to nothing, because
 * every sibling it could offer is itself `noindex`.
 *
 * A RING, not a top-6 — and that distinction is the whole fix. The directory
 * query's sort is deterministic and, at a city-wide zoom, effectively the same
 * for every ficha in the city, so "first six siblings" handed all 19 Tijuana
 * fichas the SAME six neighbours: six pages would have collected every link and
 * the other thirteen would have stayed orphans. Instead each ficha links to the
 * six that FOLLOW it in the shared ordering, wrapping at the end. Every row then
 * has ~six inbound links and no row can be skipped, from one query.
 *
 * Centred on the CITY HUB, never on this ficha's own coordinates — the ring is
 * only symmetric if every ficha in the city computes the SAME pool. Centring
 * per-ficha silently broke that: a facility at the edge of the metro saw the
 * core, so it emitted links, but the core's own boxes never reached back out to
 * it, and it stayed an orphan while appearing to be part of the ring. That is
 * exactly how La Paz and Los Cabos survived the first pass (Ahrefs re-crawl,
 * 2026-08-28). One shared centre means inbound count == outbound count for
 * every row.
 *
 * Deterministic on purpose — position in a stable list, never a shuffle. This
 * page is edge-cached (`s-maxage=600`) and hydrates on the client, so a random
 * pick would both poison the cache for the next visitor and risk a mismatch.
 *
 * Best-effort by construction: any failure returns [] and the ficha renders
 * exactly as it did before. A missing related-links block must never 500 a page
 * whose primary job is showing one facility.
 */
const fetchSiblings = async (
  client: ReturnType<typeof getApolloClient>,
  location: ILocation,
  category: DirectoryCategory,
  citySlug: string
): Promise<ISibling[]> => {
  // The hub centre, NOT `location.coordinates` — see the docblock. Falls back
  // to the facility's own point only when the slug is not a hub city, which
  // `getServerSideProps` has already rejected by the time we get here.
  const center = isDirectoryHubCity(citySlug)
    ? directoryHubCenter(citySlug)
    : undefined
  const { lat, lng } = center ?? location.coordinates ?? {}
  if (!lat || !lng) return []
  try {
    const { data } = await client.query<
      Pick<IQuery, 'directoryLocations'>,
      IQueryDirectoryLocationsArgs
    >({
      query: DIRECTORY_LOCATIONS,
      variables: {
        pagination: { from: 0, pageSize: SIBLINGS_POOL },
        searchInput: {
          center: { lat, lng },
          zoom: SIBLINGS_ZOOM,
          locationTypes: DIRECTORY_CATEGORY_LOCATION_TYPES[category]
        }
      },
      context: {
        fetchOptions: { signal: AbortSignal.timeout(SIBLINGS_TIMEOUT_MS) }
      }
    })
    // The city's roster, in the query's own stable order. Self stays IN so its
    // index can anchor the window; it is skipped when the window is walked.
    //
    // `directoryHubCityForLocation` is the membership test — the SAME router
    // the sitemap and the ficha's own canonical path use. Without it the
    // overshoot box leaks neighbours: Toluca's reaches the ZMVM, so Toluca
    // fichas linked to CDMX rows that are themselves `noindex`, judged
    // "indexable" only because they were being scored against Toluca's slug
    // rather than their own. Each row is now judged under ITS OWN city.
    const pool = ((data?.directoryLocations?.hits ?? []) as ILocation[]).filter(
      (row) => {
        if (row.id === location.id) return true
        const rowCity = directoryHubCityForLocation(row)
        return (
          rowCity === citySlug &&
          Boolean(row.title) &&
          Boolean(buildDirectoryFichaHref(row)) &&
          isDirectoryFichaIndexable({
            citySlug: rowCity,
            category,
            location: row
          })
        )
      }
    )
    const self = pool.findIndex((row) => row.id === location.id)
    // `self` is -1 only when this ficha is not in its own city's pool — a held
    // metro, or a row the gate rejects. Starting at 0 is then the right answer:
    // there is no position to rotate from, and the loop still skips self.
    const start = self === -1 ? 0 : self + 1
    const siblings: ISibling[] = []
    for (let step = 0; step < pool.length; step++) {
      if (siblings.length >= SIBLINGS_SHOWN) break
      const row = pool[(start + step) % pool.length]
      if (row.id === location.id) continue
      const href = buildDirectoryFichaHref(row)
      if (!href || !row.title) continue
      siblings.push({
        href,
        name: row.title,
        area: row.cardLocation ?? row.shortAddress ?? null
      })
    }
    return siblings
  } catch {
    return []
  }
}

export const getServerSideProps: GetServerSideProps<IProps | {}> = async (
  context
) => {
  const { locale, params, res } = context
  const { pillar, city, slug } = params ?? {}

  // The category comes from the PILLAR now. A B2B pillar has no directory
  // rows at all, so it 404s rather than rendering an empty ficha shell.
  const pillarCategory = directoryCategoryForPillar(pillar)
  if (!pillarCategory || !isDirectoryHubCity(city)) {
    return goneRedirect(directoryFallbackHref(pillar, city))
  }
  const locationId = locationIdFromDirectorySlug(slug)
  if (!locationId) return goneRedirect(directoryFallbackHref(pillar, city))

  const client = getApolloClient(context)
  let location: ILocation | undefined
  try {
    const response = await client.query<
      Pick<IQuery, 'location'>,
      IQueryLocationArgs
    >({ query: DIRECTORY_LOCATION, variables: { locationId } })
    location = response.data?.location as ILocation | undefined
  } catch (error) {
    // A deleted or opted-out row resolves to "not found" server-side. These
    // pages are `noindex`, so there is no crawler signal to protect here — the
    // only audience is a person following a stale link, and they get sent to
    // verified supply in the same city rather than a dead end.
    processRequestError(error, 'Error retrieving directory location.')
    return goneRedirect(directoryFallbackHref(pillar, city))
  }

  // `=== true`, never truthiness. This page must never render a real host
  // listing: those have prices, availability and a checkout, and dressing one
  // as an "unverified directory" entry would misrepresent live supply.
  if (!location || location.isDirectoryListing !== true) {
    // Includes the happy ending: a row that was CLAIMED flips to
    // `isDirectoryListing: false` and is a real listing now, so its old
    // directory URL must stop serving — but it should land the visitor on
    // verified supply, which is where that facility now lives.
    return goneRedirect(
      directoryFallbackHref(pillar, city, location?.coordinates)
    )
  }

  // One facility, one URL. The row's own city + category are canonical, so a
  // hand-typed or stale path redirects rather than serving a duplicate.
  const canonicalPath = buildDirectoryFichaHref(location)
  const actualCategory = directoryCategoryFromLocation(location)
  if (!canonicalPath || !actualCategory) {
    return goneRedirect(
      directoryFallbackHref(pillar, city, location.coordinates)
    )
  }
  // Also catches a facility requested under the WRONG pillar — a storage row
  // asked for at /pension-estacionamiento/... redirects to its own.
  const requestedPath = `/${pillar}/${city}/directorio/${
    Array.isArray(slug) ? slug[0] : slug
  }`
  if (canonicalPath !== requestedPath) {
    return {
      props: {},
      // 308, not 307: one facility has exactly one URL, permanently. A
      // temporary status keeps the stale variant in the crawl queue and
      // delays consolidation onto the canonical path.
      redirect: { destination: canonicalPath, permanent: true }
    }
  }

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=600, stale-while-revalidate=3600'
  )

  return {
    props: {
      location,
      category: actualCategory,
      citySlug: city,
      fichaPath: canonicalPath,
      ...(await Promise.all([
        fetchSiblings(client, location, actualCategory, city),
        fetchHasVerifiedSupply(client, location, actualCategory)
      ]).then(([siblings, hasVerifiedSupply]) => ({
        siblings,
        hasVerifiedSupply
      }))),
      ...(await serverSideTranslations(locale ?? 'es', [
        'common',
        'nav',
        'footer',
        'platform_stats',
        'directorio'
      ]))
    }
  }
}

export default React.memo(DirectoryFichaPage)
